import { SquareNewWindowIcon } from "@codat/orchard-ui";
import { Product } from "api/schema/productsSchema";
import React from "react";
import { matchPath } from "react-router-dom";

import { paths } from "components/App/paths";
import { NavPermissionMap } from "components/NavContainer/utils";
import { SidebarGroup } from "components/Sidebar/Sidebar";

const getSidebarGroups = (
  location: string,
  navPermissions: NavPermissionMap,
  products: Product[],
  featureStatus?: Record<string, boolean>
): SidebarGroup[] | undefined => {
  const isMonitorPage = matchPath(location, {
    path: Object.values(paths.monitor),
  });

  const isSettingsPage = matchPath(location, {
    path: Object.values(paths.settings),
  });

  const isAccountSettingsPage = matchPath(location, {
    path: paths.account.SETTINGS,
  });

  const isSettingsOverviewPage = matchPath(location, {
    path: paths.settings.OVERVIEW,
    exact: true,
  });

  const isDevelopersPage = matchPath(location, {
    path: Object.values(paths.developers),
  });

  const isProductsPage = matchPath(location, {
    path: [
      paths.products.ACCOUNTING_API,
      paths.products.BANKING_API,
      paths.products.COMMERCE_API,
    ],
  });

  if (isMonitorPage) {
    return getMonitorPageSidebar(navPermissions, products);
  }

  if (isSettingsPage && !isSettingsOverviewPage && !isAccountSettingsPage) {
    return getSettingsPageSidebar(navPermissions, products);
  }

  if (isDevelopersPage) {
    return getDevelopersPageSidebar(navPermissions);
  }

  if (isProductsPage) {
    return getProductsPageSidebar(navPermissions, location, featureStatus);
  }
};

const getMonitorPageSidebar = (
  navPermissions: NavPermissionMap,
  products: Product[]
) => {
  const { monitor } = navPermissions;
  return [
    monitor.events.permission && {
      groupName: "Webhooks",
      items: [
        monitor.events.permission && {
          name: "Events",
          route: monitor.events.path,
        },
      ].filter(Boolean),
    },
    monitor.connectorHealth.permission && {
      groupName: "Health",
      items: [
        {
          name: "Offline connectors",
          route: monitor.connectorHealth.path,
        },
      ],
    },
    monitor.authFlowMonitoring.permission && {
      groupName: "Auth flow",
      items: [
        {
          name: "Overview",
          route: monitor.authFlowMonitoring.path,
        },
      ],
    },
    isProductEnabled("managed-bank-feeds", products) && {
      groupName: "Managed bank feeds",
      items: [
        {
          name: "Syncs",
          route: monitor.managedBankFeedsSyncs.path,
        },
      ],
    },
  ].filter(Boolean) as NonNullable<SidebarGroup[]>;
};

const getSettingsPageSidebar = (
  navPermissions: NavPermissionMap,
  products: Product[]
) => {
  const { settings } = navPermissions;

  return [
    settings.products.permission && {
      groupName: "Products",
      items: [
        ...products
          .filter(
            (product) => product.status === "Enabled" && product.hasSettings
          )
          .map((product) => ({
            name: product.name,
            route: `${paths.settings.PRODUCTS}/${product.key}`,
          })),
        {
          name: "Manage products",
          route: paths.settings.MANAGE_PRODUCTS,
        },
      ],
    },
    (settings.integrations.permission ||
      settings.otherIntegrations.permission ||
      settings.bankFeeds.permission ||
      settings.dataTypes.permission) && {
      groupName: "Integrations",
      items: [
        settings.integrations.permission && {
          name: "Accounting",
          route: paths.settings.INTEGRATIONS_ACCOUNTING,
        },
        settings.bankFeeds.permission && {
          name: "Bank feeds",
          route: settings.bankFeeds.path,
        },
        settings.integrations.permission && {
          name: "Banking",
          route: paths.settings.INTEGRATIONS_BANKING,
        },
        settings.integrations.permission && {
          name: "Commerce",
          route: paths.settings.INTEGRATIONS_COMMERCE,
        },
        settings.otherIntegrations.permission && {
          name: "Other integrations",
          route: settings.otherIntegrations.path,
        },
        settings.dataTypes.permission && {
          name: "Data types",
          route: settings.dataTypes.path,
        },
      ].filter(Boolean),
    },
    settings.linkSettings.permission && {
      groupName: "Auth flow",
      items: [
        {
          name: "Branding",
          route: settings.branding.path,
        },
        {
          name: "Redirects",
          route: settings.redirects.path,
        },
        {
          name: "Link",
          route: settings.linkSettings.path,
        },
      ],
    },
    (settings.rules.permission || settings.webhookSecurity.permission) && {
      groupName: "Webhooks",
      items: [
        settings.rules.permission && {
          name: "Rules",
          route: settings.rules.path,
        },
        settings.webhookSecurity.permission && {
          name: "Security",
          route: settings.webhookSecurity.path,
        },
      ].filter(Boolean),
    },
    settings.users.permission && {
      groupName: "Organization settings",
      items: [
        settings.users.permission && {
          name: "Users",
          route: settings.users.path,
        },
        settings.billing.permission && {
          name: "Billing",
          route: settings.billing.path,
        },
      ].filter(Boolean),
    },
  ].filter(Boolean) as NonNullable<SidebarGroup[]>;
};

const getDevelopersPageSidebar = (navPermissions: NavPermissionMap) => {
  const { developers } = navPermissions;

  return [
    {
      groupName: "API",
      items: [
        developers.apiKeys.permission && {
          name: "API keys",
          route: developers.apiKeys.path,
        },
        developers.deprecations.permission && {
          name: "API deprecations",
          route: developers.deprecations.path,
        },
        {
          name: "API status",
          route: null,
          href: "https://status.codat.io/",
          indicator: <SquareNewWindowIcon />,
        },
      ].filter(Boolean),
    },
    {
      groupName: "Resources",
      items: [
        {
          name: "Getting started guide",
          route: null,
          href: "https://docs.codat.io/get-started/first-steps",
          indicator: <SquareNewWindowIcon />,
        },
        {
          name: "API reference",
          route: null,
          href: "https://docs.codat.io/using-the-api/overview",
          indicator: <SquareNewWindowIcon />,
        },
        {
          name: "Postman",
          route: null,
          href: "https://postman.codat.io/#3187c96c-2bdc-4aa4-b43c-08e5ecb166e4",
          indicator: <SquareNewWindowIcon />,
        },
      ],
    },
  ].filter(Boolean) as NonNullable<SidebarGroup[]>;
};

const getProductsPageSidebar = (
  navPermissions: NavPermissionMap,
  location: string,
  featureStatus?: Record<string, boolean>
): SidebarGroup[] => {
  const { settings } = navPermissions;
  const productPagesEnabled =
    (featureStatus && featureStatus["PortalUI.ProductPagesEnabled"]) || false;

  const calculateProductName = (): [string, string, string] => {
    if (location.includes("accounting-api")) {
      return [
        "Accounting",
        paths.settings.INTEGRATIONS_ACCOUNTING,
        paths.products.ACCOUNTING_API,
      ];
    }

    if (location.includes("banking-api")) {
      return [
        "Banking",
        paths.settings.INTEGRATIONS_BANKING,
        paths.products.BANKING_API,
      ];
    }

    if (location.includes("commerce-api")) {
      return [
        "Commerce",
        paths.settings.INTEGRATIONS_COMMERCE,
        paths.products.COMMERCE_API,
      ];
    }

    return ["", "", ""];
  };

  const [productName, integrationSettingsRoute, productsRoute] =
    calculateProductName();

  return [
    {
      groupName: "Learn",
      items: [
        {
          name: "Product overview",
          route: `${productsRoute}/product-overview`,
        },
        {
          name: "Developer resources",
          route: `${productsRoute}/developer-resources`,
        },
        {
          name: "API coverage",
          route: `${productsRoute}/api-coverage`,
        },
      ],
    },
    {
      groupName: "Use",
      items: [
        productPagesEnabled && {
          name: "Companies",
          route: "companies",
        },
        settings.dataTypes.permission && {
          name: "Data type settings",
          route: `${
            paths.settings.DATA_TYPES
          }?view=${productName.toLowerCase()}`,
        },
        {
          name: "Integration settings",
          route: integrationSettingsRoute,
        },
      ].filter(Boolean),
    },
    {
      groupName: "Monitor",
      items: [
        {
          name: "API status",
          route: null,
          href: "https://status.codat.io",
          indicator: <SquareNewWindowIcon />,
        },
      ],
    },
  ].filter(Boolean) as NonNullable<SidebarGroup[]>;
};

const isProductEnabled = (productKey: string, products: Product[]) =>
  products.filter(
    (product) => product.key === productKey && product.status === "Enabled"
  ).length > 0;

export default getSidebarGroups;
